import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { API_ENDPOINTS } from '../constants/config';
import { getStoredAuth } from '../utils/authStorage';

const PAGE_NAMES = {
  '/dashboard/dashboard': 'Dashboard',
  '/dashboard/students': 'Students',
  '/dashboard/teachers': 'Teachers',
  '/dashboard/staff': 'Staff',
  '/dashboard/teacher-attendance': 'Teacher Attendance',
  '/dashboard/fees': 'Fees',
  '/dashboard/classes': 'Classes',
  '/dashboard/subjects': 'Subjects',
  '/dashboard/sections': 'Sections',
  '/dashboard/inventory': 'Inventory',
  '/dashboard/announcements': 'Announcements',
  '/dashboard/leave': 'Leave',
  '/dashboard/reports': 'Reports',
  '/dashboard/settings': 'Settings',
  '/dashboard/transport': 'Transport',
  '/dashboard/salary': 'Salary',
  '/dashboard/schools': 'Manage Customers',
  '/dashboard/system-logs': 'System Logs',
  '/dashboard/bus-tracking': 'Bus Tracking',
  '/dashboard/app-version': 'App Version',
  '/dashboard/public-leads': 'Public Leads',
  '/dashboard/super-school-admins': 'Super Customer Admins',
  '/dashboard/user-analytics': 'User Analytics',
};

function getPageName(path) {
  if (PAGE_NAMES[path]) return PAGE_NAMES[path];
  // Try first 2 segments (handles /dashboard/students/123 → Students)
  const base = '/' + path.split('/').filter(Boolean).slice(0, 2).join('/');
  if (PAGE_NAMES[base]) return PAGE_NAMES[base];
  // Fallback: capitalize last non-ID segment
  const segments = path.split('/').filter(Boolean);
  const last = segments.pop() || 'Unknown';
  // Skip MongoDB ObjectId-like segments
  if (/^[a-f0-9]{24}$/.test(last) && segments.length) {
    const prev = segments.pop() || 'Unknown';
    return prev.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function usePageTracking() {
  const location = useLocation();
  const pageStart = useRef(null);
  const prevPage = useRef(null);

  useEffect(() => {
    const path = location.pathname;
    const now = Date.now();

    // Send the previous page's visit duration
    if (prevPage.current !== null && pageStart.current !== null) {
      const duration = Math.floor((now - pageStart.current) / 1000);
      if (duration > 0) {
        const { user } = getStoredAuth();
        if (user) {
          axiosInstance.post(API_ENDPOINTS.pageVisits, {
            page: prevPage.current,
            pageName: getPageName(prevPage.current),
            duration,
            enteredAt: new Date(pageStart.current).toISOString(),
            platform: 'web',
          }).catch(() => {});
        }
      }
    }

    prevPage.current = path;
    pageStart.current = now;
  }, [location.pathname]);
}
